import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';

import { ApiError, accountApi, usersApi, type AccountView } from '../api/client';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FormField,
  Input,
  LanguageSwitcher,
  PageHeader,
  PageLayout,
  PasswordInput,
  Section,
} from '../components';
import { useI18n } from '../i18n';
import { useSession } from '../auth/session-context';
import { formatMoney } from '../format';

/**
 * Einstellungen.
 *
 * Bewusst klein gehalten und in vier klare Bereiche geteilt: Konto, Sprache,
 * Sicherheit und - deutlich abgesetzt - das Löschen des Kontos.
 *
 * Das Guthaben ist hier nur eine Angabe; verwaltet wird es unter einem
 * eigenen Navigationspunkt, damit es nicht in den Einstellungen verschwindet.
 */
export function AccountPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, logout, handleApiError } = useSession();
  const nameId = useId();
  const aktuellId = useId();
  const neuId = useId();

  const [account, setAccount] = useState<AccountView | null>(null);
  const [accountName, setAccountName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogFehler, setDialogFehler] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;
  const isOwner = principal?.kind === 'tenant_user' && principal.isOwner;

  const run = useCallback(
    async (action: () => Promise<void>, imDialog = false) => {
      setError(null);
      setDialogFehler(null);
      setBusy(true);
      try {
        await action();
      } catch (caught) {
        handleApiError(caught);
        const text = caught instanceof ApiError ? tError(caught.code) : tError(undefined);
        if (imDialog) {
          setDialogFehler(text);
        } else {
          setError(text);
        }
      } finally {
        setBusy(false);
      }
    },
    [handleApiError, tError],
  );

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    void run(async () => {
      const response = await accountApi.get();
      setAccount(response.account);
      setAccountName(response.account.accountName ?? '');
    });
  }, [isAdmin, run]);

  const saveName = (event: FormEvent): void => {
    event.preventDefault();
    void run(async () => {
      const response = await accountApi.rename(accountName.trim() === '' ? null : accountName);
      setAccount(response.account);
      setMessage(t('account.nameSaved'));
    });
  };

  const changePassword = (event: FormEvent): void => {
    event.preventDefault();
    void run(async () => {
      await usersApi.changeOwnPassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setMessage(t('account.passwordSaved'));
    });
  };

  return (
    <PageLayout width="mittel">
      <PageHeader title={t('account.title')} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      {account ? (
        <Card>
          <Section title={t('account.title')}>
            <dl className="facts">
              <dt>{t('account.customerNumber')}</dt>
              <dd>{account.customerNumber}</dd>
              <dt>{t('account.email')}</dt>
              <dd>{account.ownerEmail}</dd>
              <dt>{t('account.balance')}</dt>
              <dd>{formatMoney(account.balance)}</dd>
            </dl>

            <form onSubmit={saveName} className="form form-inline">
              <FormField label={t('account.name')} htmlFor={nameId}>
                <Input id={nameId} value={accountName} onChange={setAccountName} />
              </FormField>
              <Button type="submit" disabled={busy}>
                {t('action.save')}
              </Button>
            </form>
          </Section>
        </Card>
      ) : null}

      <Card>
        <Section title={t('account.language')}>
          <LanguageSwitcher
            onChange={(locale) => {
              void usersApi.setLocale(locale).catch(() => undefined);
            }}
          />
        </Section>
      </Card>

      <Card>
        <Section title={t('account.changePassword')}>
          <form onSubmit={changePassword} className="form">
            <FormField label={t('account.currentPassword')} htmlFor={aktuellId}>
              <PasswordInput id={aktuellId} value={currentPassword} onChange={setCurrentPassword} />
            </FormField>
            <FormField label={t('account.newPassword')} htmlFor={neuId}>
              <PasswordInput
                id={neuId}
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
              />
            </FormField>
            <div className="row-actions">
              <Button type="submit" variant="primary" disabled={busy}>
                {t('action.save')}
              </Button>
            </div>
          </form>
        </Section>
      </Card>

      {isOwner ? (
        <Card tone="gefahr">
          <Section title={t('account.delete')} description={t('account.deleteWarning')}>
            <div className="row-actions">
              <Button variant="danger" onClick={() => setLoeschenOffen(true)}>
                {t('account.delete')}
              </Button>
            </div>
          </Section>
        </Card>
      ) : null}

      {loeschenOffen ? (
        <ConfirmDialog
          title={t('dialog.accountDelete')}
          message={t('account.deleteWarning')}
          confirmLabel={t('account.deleteConfirm')}
          busy={busy}
          error={dialogFehler}
          onCancel={() => {
            setLoeschenOffen(false);
            setDeletePassword('');
          }}
          onConfirm={() =>
            void run(async () => {
              await accountApi.remove(deletePassword);
              await logout();
            }, true)
          }
        >
          <FormField label={t('account.deletePassword')}>
            <PasswordInput value={deletePassword} onChange={setDeletePassword} />
          </FormField>
        </ConfirmDialog>
      ) : null}
    </PageLayout>
  );
}
